/**
 * Shared CI/GitOps types — contract between backend and CLI.
 *
 * Source of truth: workers/backend/src/routes/api/ci/types.ts
 * The backend may have extra optional fields (e.g. connection_id, target_id)
 * that the CLI ignores — this file carries the full (superset) definitions.
 */

import { z } from 'zod';
import {
  validateToolInputSchema,
  containsKanbanPlaceholders,
  extractToolParameters,
} from '../tool-schema-validator';
import { kanbanStatusesArraySchema, lanesArraySchema, validateLaneReferences } from './setup/kanban';

// =============================================
// Kanban status types
// Field names match the existing JSONB schema in the database and frontend
// (KanbanStatusCard.tsx, FollowupCard.tsx). The mixed casing (camelCase booleans,
// snake_case strings) is inherited from the original frontend implementation.
// =============================================

export interface HubAsCodeKanbanFollowup {
  id?: string;
  order: number;
  type: 'inactivity' | 'before_event';
  threshold: number;
  timeUnit: 'seconds' | 'minutes' | 'hours' | 'days';
  instructions: string;
  excludedWeekDays?: number[];
  excludeHolidays?: boolean;
  excludedTimeStart?: string;
  excludedTimeEnd?: string;
  /** 'agent_generated' (default) feeds `instructions` to the LLM; 'direct' sends `direct_text` verbatim. */
  delivery_mode?: 'agent_generated' | 'direct';
  /** Verbatim user-facing text. Required when `delivery_mode='direct'`. */
  direct_text?: string;
  /** Approved Meta WhatsApp template id; consulted only outside the WA 24h window. */
  template_whatsapp_id?: string | null;
  /** Default `'route_to_team'` when omitted. Only consulted on `direct` mode + windowed channels outside the window. */
  outside_window_policy?: 'route_to_team' | 'skip';
  /** Default true — followup turn includes tool schemas. Set to false to strip them and save tokens. No effect on `direct` mode. */
  include_tools?: boolean;
}

export interface HubAsCodeKanbanStatus {
  /** Stable identifier — set once at create, never edited. Lowercase slug, used in
   *  `conversation.kanban_status`, ClickHouse `data.meta.kanban_status`, tool params,
   *  and `allowed_next_statuses` references. */
  slug: string;
  /** Display label — shown in UI and tool descriptions. Freely editable. */
  name: string;
  order?: number;
  color?: string;
  isInitialStatus?: boolean;
  triggersAgentResponse?: boolean;
  allowsAgentUpdate?: boolean;
  isTerminalStatus?: boolean;
  isSchedulingStatus?: boolean;
  eventName?: string;
  /** Template prose injected into the agent turn on transition. Supports
   *  `{{path.to.field}}` placeholders (dotted, whitespace-tolerant) and the
   *  reserved `{{additional_data}}` placeholder which expands to the full
   *  payload as 2-space-indented JSON. */
  additional_instructions?: string;
  /** JSON Schema (Draft 2020-12) defining the form fields collected at
   *  transition time. The collected payload is interpolated into
   *  `additional_instructions` and emitted to the agent as a
   *  `<system_additional_instructions>` block. Top-level property name
   *  `additional_data` is reserved (collides with the dump placeholder).
   *  Capped at 16 KB when JSON-serialized. */
  additional_context_schema?: Record<string, unknown>;
  followups?: HubAsCodeKanbanFollowup[];
  /** Slugs this status can transition to. `undefined` = unrestricted (any next).
   *  Empty array is rejected by the validator — use `isTerminalStatus: true` for
   *  no outbound transitions. */
  allowed_next_statuses?: string[];
  /** Optional presentational lane this status belongs to (slug of a hub `lanes[]`
   *  entry). Board-grouping only; not a transition constraint, not agent-visible.
   *  The initial status must not set this. */
  lane_slug?: string;
}

/** A presentational lane — a hub-level grouping of kanban statuses for board
 *  filtering. Lives inside the hub config (NOT a separate hub-as-code entity like
 *  the eval `journeys`, which own their own YAML files). */
export interface HubAsCodeLane {
  slug: string;
  name: string;
  color?: string;
  order?: number;
}

// =============================================
// HubAsCode payload — JSON representation of hub.yaml + agents/*.yaml
// =============================================

export interface HubAsCodePayload {
  version: number;
  hub_id: string;
  hub_environment: string;
  preview_label?: string | null;
  branch_name?: string | null;
  /**
   * For a preview hub, the id of its linked production hub (null until first
   * publish). Lets the CLI mirror production read-only on every preview pull.
   * Always null on a production hub's own payload (it has no parent).
   */
  production_hub_id?: string | null;

  hub: {
    name: string;
    description?: string;
    hub_type?: string;
    ai_mode?: string;
    timezone?: string;
    /** Hub language (en|pt|es) — localizes the channel-contact-access pending notice. */
    language?: string;
    /** Gate for who may approve/block channel contacts: 'admin' (default) | 'team'. */
    access_approval_role?: string;
    /** Override text for the one-time "your access is pending" notice (else localized default). */
    access_request_message?: string | null;
    non_app_permission?: string;
    mcp_access?: string;
    kanban_statuses?: HubAsCodeKanbanStatus[];
    /** Presentational lane definitions for board grouping (see HubAsCodeLane). A
     *  hub-config field, NOT a separate YAML entity like `journeys`. */
    lanes?: HubAsCodeLane[];
    // flag_conditions relocated to the conversation_evaluator AGENT (see HubAsCodeAgent.flag_conditions).
    // summarization_threshold_tokens relocated to the summarizer AGENT (see HubAsCodeAgent.summarization_threshold_tokens).
    // monitor_config relocated to the monitor AGENT (see HubAsCodeAgent.monitor_config).
    /** Days of inactivity (no user/team messages) before a conversation is force-closed.
     *  Range 1..180, default 7. Mandatory across every hub. */
    auto_close_inactive_days?: number;
    /** Days an ended conversation's ConversationDO is kept alive so it can still receive
     *  post-hoc annotations before the cleanup cron deletes it. Range 1..30, default 7.
     *  Archival is unaffected (still at close). */
    conversation_retention_days?: number;
    /** Org tag slug names (not UUIDs). Resolved against OrgDO `org_tag` on push;
     *  unknown names are rejected. Hub tags gate which org credentials this hub
     *  can resolve via the symmetric overlap rule (see SKILL connections doc). */
    tags?: string[];
  };

  agents?: HubAsCodeAgent[];
  resources?: HubAsCodeResource[];
  states?: HubAsCodeState[];
  teams?: HubAsCodeTeam[];
  evals?: HubAsCodeEval[];
  journeys?: HubAsCodeJourney[];
  outbound_contacts?: HubAsCodeOutboundContact[];
  outbound_lists?: HubAsCodeOutboundList[];
  outbound_schedules?: HubAsCodeOutboundSchedule[];

  /** Connections serve as reference targets for `connection_id` fields on agents
   *  and custom tools. Non-secret configuration fields (base_url, headers, settings,
   *  etc.) are round-trippable via pull/push. The bound org credential round-trips
   *  by name via `credential`; the raw secret is never exported. */
  connections?: Array<{
    id?: string;
    name: string;
    type: string;
    service?: string;
    /** Pin an org credential by display name (round-trips on pull). */
    credential?: string;
    /** Bearer Token org credential for dual-credential (API Key + access token)
     *  connectors. Push-only: not round-tripped on pull (the connection row stores
     *  a single credential id, so only the primary `credential` is re-emitted). */
    access_token_credential?: string;
    /** Opt into a deliberately unauthenticated connection for optional-auth connectors
     *  (e.g. a no-auth MCP server). Suppresses the "created without authentication"
     *  push warning. Must not be combined with `credential`. */
    no_auth?: boolean;
    base_url?: string;
    headers?: Record<string, string>;
    settings?: Record<string, unknown>;
    account_sid?: string;
    enabled?: boolean;
    authentication_type?: string;
  }>;
}

export interface HubAsCodeAgent {
  id?: string;
  name: string;
  role: string;
  connection: string;
  connection_id?: string;
  instructions?: string;
  additional_context_template?: string;
  enabled?: boolean;
  include_message_timestamps?: boolean;
  settings?: Record<string, unknown>;
  response_format?: {
    type: string;
    schema_name: string;
    schema_json: Record<string, unknown>;
  };
  tools?: {
    native?: Array<string | HubAsCodeNativeTool>;
    delegation?: Array<{
      id?: string;
      type: string;
      tool: string;
      target: string;
      target_id?: string;
    }>;
    custom?: HubAsCodeCustomTool[];
    /**
     * MCP tools discovered from a `Tool - MCP` connection, assigned to this agent.
     * Identity key is `(connection, name)` (an MCP tool name is unique per server).
     *
     * **Omitted vs present (mirrors `evaluation_variables`):** when the `mcp` key
     * is OMITTED, existing MCP tool rows on the agent are left untouched (so tools
     * assigned in the Platform UI survive a push that doesn't manage them). When
     * PRESENT (even as `[]`), the list is AUTHORITATIVE — listed-but-missing tools
     * are created, unlisted MCP tools are removed, matched ones kept. This differs
     * from `native`/`custom` (whose absence deletes them) because MCP tools are
     * dual-origin (UI + GitOps).
     *
     * Push discovers the connection's tools on demand (equivalent to the UI's
     * "Sync MCP tools" action), so a single push can create the connection AND
     * assign its tools — the MCP server must be reachable.
     */
    mcp?: HubAsCodeMcpTool[];
  };
  resources?: HubAsCodeAgentResource[];
  /**
   * Evaluation variables (a.k.a. agent parameters) — the structured fields an
   * evaluator agent writes per conversation/message. Round-trippable via
   * pull/push. Identity key is `name` (unique per agent). When the key is
   * OMITTED from YAML, existing variables are left untouched; when PRESENT
   * (even as `[]`), the list is treated as authoritative and replaces the
   * agent's current variables. Primarily used by `conversation_evaluator` /
   * `message_evaluator` agents.
   */
  evaluation_variables?: HubAsCodeEvaluationVariable[];
  /**
   * Conversation-flagging conditions evaluated post-close (OR semantics). A true
   * agent parameter on the `conversation_evaluator` agent (relocated from the hub
   * section). Round-trippable via pull/push. Meaningful only on the
   * `conversation_evaluator` agent; ignored elsewhere.
   */
  flag_conditions?: Array<{ variable: string; operator: '=' | '!='; value: string | number }>;
  /**
   * Token threshold that fires the rolling summarizer (effective tokens =
   * input + cache_read + cache_creation; default 120000). A true agent parameter
   * on the `summarizer` agent (relocated from the hub section). Round-trippable via
   * pull/push. Meaningful only on the `summarizer` agent; ignored elsewhere.
   */
  summarization_threshold_tokens?: number;
  /**
   * Monitor scheduling + flagging config ({delay_seconds, flag_conditions}). A true
   * agent parameter on the `monitor` agent (relocated from the hub section).
   * Round-trippable via pull/push. `delay_seconds` >= 10. Meaningful only on the
   * `monitor` agent; ignored elsewhere.
   */
  monitor_config?: {
    delay_seconds: number;
    flag_conditions?: Array<{ variable: string; operator: '=' | '!='; value: string | number }>;
  } | null;
}

// Canonical key lists for HubAsCodeAgent and its `tools` block, co-located with
// the interface so the two are edited together. The CI diff engine derives its
// unknown-key warnings (gh #2505) from these — without a single source of truth,
// a hand-maintained copy silently drifts and emits false "unknown key" warnings
// on valid configs once a new field lands. The config validator is `.passthrough()`
// (no runtime schema to reflect), so parity is enforced at COMPILE time below:
//   - `satisfies readonly (keyof …)[]` rejects typo'd/extra entries
//   - `_Expect<_Exhaustive<…>>` rejects a key present on the interface but missing here
// Add a field to the interface → tsc fails until it's added to the matching list.
type _Expect<T extends true> = T;
type _Exhaustive<Keys extends PropertyKey, Listed extends PropertyKey> =
  Exclude<Keys, Listed> extends never ? true : false;

export const HUB_AS_CODE_AGENT_KEYS = [
  'id', 'name', 'role', 'connection', 'connection_id', 'instructions',
  'additional_context_template', 'enabled', 'include_message_timestamps',
  'settings', 'response_format', 'tools', 'resources', 'evaluation_variables',
  'flag_conditions', 'summarization_threshold_tokens', 'monitor_config',
] as const satisfies readonly (keyof HubAsCodeAgent)[];
type _AgentKeysExhaustive = _Expect<_Exhaustive<keyof HubAsCodeAgent, (typeof HUB_AS_CODE_AGENT_KEYS)[number]>>;

export const HUB_AS_CODE_AGENT_TOOL_KEYS = [
  'native', 'delegation', 'custom', 'mcp',
] as const satisfies readonly (keyof NonNullable<HubAsCodeAgent['tools']>)[];
type _AgentToolKeysExhaustive = _Expect<_Exhaustive<keyof NonNullable<HubAsCodeAgent['tools']>, (typeof HUB_AS_CODE_AGENT_TOOL_KEYS)[number]>>;

/**
 * One evaluation variable on an agent. Maps to a HubDO `agent_parameter` row.
 * Defaults on read (push): `type` → 'string', `required` → true,
 * `is_summary` → false, everything else unset/empty. The emit side (pull)
 * omits any field equal to its default so round-trips stay clean.
 */
export interface HubAsCodeEvaluationVariable {
  /** Variable name the agent writes — unique within the agent. Required. */
  name: string;
  /** 'string' (default) | 'number' | 'boolean' | 'integer' | 'enum'. */
  type?: string;
  description?: string;
  /** Human-facing display label (Analytics columns, UI). */
  label?: string;
  /** Allowed values when `type: enum`. Ignored for other types. */
  enum?: string[];
  /** Defaults to true when omitted (mirrors the UI's new-variable default). */
  required?: boolean;
  /**
   * Analytics grouping. Conventional values: quality | satisfaction |
   * compliance | categorization. Free-form to preserve system-internal
   * markers (e.g. `eval` on message-evaluator defaults) across round-trips.
   */
  category?: string | null;
  /**
   * Marks the rolling conversation-summary field. Only honored for
   * `type: string`; coerced to false otherwise.
   */
  is_summary?: boolean;
}

/**
 * Native tool entry. The string form (e.g. `"send_text_message"`) keeps the
 * platform/catalog defaults. The object form lets users override per-instance
 * fields — only `keep_in_history` and `expose_schema_to_llm` are user-managed;
 * other native-tool fields (schema, instructions, operation) come from the
 * catalog and are not round-tripped. Override fields are diff-applied only
 * when explicitly present in YAML — omitted means "leave as is".
 */
export interface HubAsCodeNativeTool {
  id?: string;
  name: string;
  keep_in_history?: boolean;
  expose_schema_to_llm?: boolean;
}

/**
 * An MCP tool assigned to an agent (under `tools.mcp`). The tool's schema,
 * description, and config come from the connection's discovered `mcp_tool`
 * catalog — only the reference round-trips through YAML. `connection` (display
 * name) disambiguates which MCP server the tool comes from; `connection_id` and
 * `id` (the tool-row id) are emitted on pull for stable matching.
 */
export interface HubAsCodeMcpTool {
  id?: string;
  name: string;
  connection?: string;
  connection_id?: string;
}

export interface HubAsCodeCustomTool {
  id?: string;
  name: string;
  description?: string;
  method?: string;
  path?: string;
  body_format?: string;
  connection?: string;
  connection_id?: string;
  headers?: Record<string, unknown>;
  query_params?: Record<string, unknown>;
  body_params?: Record<string, unknown>;
  config?: Record<string, unknown>;           // OpenAI function schema (tool_config)
  keep_in_history?: boolean;                   // default false; controls whether tool call+result remain in LLM history
  instructions?: string;
  composed_tools?: Array<{
    tool_name: string;
    mappings: Record<string, string | { source: string; type?: string; description?: string }>;
    appended_description?: string;
    success_path?: string;
  }> | null;
  enabled?: boolean;
  expose_schema_to_llm?: boolean;
}

export interface HubAsCodeState {
  id?: string;
  /**
   * Stable slug — immutable wire identifier. CI diff uses (slug, scope) as the
   * identity key so renaming `name` is detected as an in-place edit rather than
   * a delete + create. Optional on the wire: when omitted the push pipeline
   * derives slugify(name) (the documented hub.yaml shape authors states with
   * only a `name`). Typed `string` because every resolved state carries one.
   */
  slug: string;
  name: string;
  scope: string;
  description?: string;
  enabled?: boolean;
  inject_in_last_message?: boolean;
  json_schema?: Record<string, unknown>;
  initial_value?: Record<string, unknown> | null;
}

export interface HubAsCodeTeam {
  id?: string;           // hub_team_id (stable, set by pull)
  name: string;          // team_name
  access_rule?: 'anytime' | 'anytime_if_delegated_once' | 'only_while_delegated';
  initial_team?: boolean;
  display_only_if_claimed?: boolean;  // conversation_display_only_if_claimed
}

export interface HubAsCodeResource {
  id?: string;
  name: string;
  type?: string;              // 'knowledge' (default) | 'skill'
  description?: string;
  enabled?: boolean;
  user_browsable?: boolean;
  skill_name?: string;        // skill-only: slugified unique name (auto-derived from name if omitted)
  files?: HubAsCodeResourceFile[];
}

export interface HubAsCodeResourceFile {
  id?: string;                // resource_file_id (stable, set by pull)
  path: string;               // relative path within resource, e.g. "images/logo.png"
  title?: string;             // display name (defaults to filename)
  content?: string;           // text file content (push: inlined, pull: inlined)
  content_base64?: string;    // binary file content (push only)
  download_url?: string;      // signed R2 URL (pull only, binary files)
  mime_type?: string;
  file_size?: number;
  hash?: string;              // SHA-256
}

export interface HubAsCodeAgentResource {
  name: string;                        // resource display name
  resource_id?: string;                // stable ID
  priority?: number;
  enabled?: boolean;
  include_structure_in_prompt?: boolean;
  use_native_integration?: boolean;    // skill-only: tool-based (false) vs provider-native (true)
  write_enabled?: boolean;             // grants the agent edit access (download_file/upload_file)
}

// =============================================================================
// ORG-AS-CODE — organization-scoped resources synced via `wayai org push/pull`
// (CLI `org/` directory ⇄ `/api/ci/org-resources/*`). Org resources are the
// org-level analog of hub resources: managed in OrgDO, shadowed into linked
// hubs. They carry no agent links (hubs link the shadow), but DO carry
// `environment`/`tags` (which gate hub eligibility) and explicit folders (org
// folders are a real table with metadata, unlike path-implicit hub folders).
// =============================================================================

export interface OrgAsCodeResourceFolder {
  id?: string;                // org_resource_folder_id (stable, set by pull)
  path: string;               // folder path relative to resource root, e.g. "docs/guides"
  description?: string;       // folder_description
}

export interface OrgAsCodeResource {
  id?: string;                // org_resource_id (stable, set by pull)
  name: string;
  type?: string;              // 'knowledge' (default) | 'skill'
  description?: string;
  enabled?: boolean;
  user_browsable?: boolean;
  skill_name?: string;        // skill-only: slugified unique name (auto-derived from name if omitted)
  environment?: 'all' | 'preview' | 'production';  // which hub environments may link this resource
  tags?: string[];            // org_tag UUIDs gating hub eligibility
  folders?: OrgAsCodeResourceFolder[];             // explicit folders (preserves empty/described folders)
  files?: HubAsCodeResourceFile[];                 // identical shape to hub resource files
}

export interface OrgResourcesPayload {
  version: 1;
  organization_id?: string;
  resources?: OrgAsCodeResource[];
}

export interface HubAsCodeOutboundContact {
  id?: string;
  name: string;
  phone?: string;
  email?: string;
  instagram_sid?: string;
  tags?: string[];
  enabled?: boolean;
}

export interface HubAsCodeOutboundList {
  id?: string;
  name: string;
  description?: string;
  contacts?: string[];       // contact names
}

export interface HubAsCodeOutboundSchedule {
  id?: string;
  name: string;
  description?: string;
  cron_expression: string;
  timezone?: string;
  list: string;              // list name reference
  list_id?: string;
  channel_type: string;
  connection?: string;       // connection name reference
  connection_id?: string;
  execution_mode?: string;
  template_whatsapp?: string; // template name reference
  template_whatsapp_id?: string;
  initial_message?: string;
  agent?: string;            // agent name reference
  agent_id?: string;
  trigger_message?: string;
  enabled?: boolean;
}

export interface HubAsCodeEval {
  id?: string;
  name: string;
  path?: string | null;
  agent: string;                   // agent display name
  agent_id?: string;               // stable agent UUID
  runs?: number;                   // default 1
  enabled?: boolean;               // default true
  history?: Array<{ role: string; content?: string | null; tool_calls?: unknown; tool_call_id?: string }>;
  input: { role: string; content?: string | null; tool_calls?: unknown };
  expected: { role: string; content?: string | null; tool_calls?: unknown };
  evaluator_instructions?: string;
}

/**
 * One turn of a journey's happy-path transcript — the replayed message-history
 * shape plus an optional stable `step_id` (server-minted on each step's
 * concluding assistant turn; carried so per-step `step_config` overrides and the
 * derived evals stay keyed across a pull → push round-trip).
 */
export interface HubAsCodeJourneyTurn {
  role: string;
  content?: string | null;
  tool_calls?: unknown;
  tool_call_id?: string;
  step_id?: string;
}

/** Per-step override config, keyed by `step_id` (mirrors the eval `runs` field). */
export interface HubAsCodeJourneyStepOverride {
  runs?: number;
  evaluator_instructions?: string | null;
}

/**
 * An eval JOURNEY — a stored happy-path transcript that materializes into
 * single-turn evals. First-class hub-as-code entity (one YAML file per journey;
 * the journey owns its managed scenario_set, which is NOT authored). The derived
 * evals stay server-managed and are excluded from the eval diff.
 */
export interface HubAsCodeJourney {
  id?: string;                       // journey_id (stable, set by pull; primary match key)
  name: string;                      // journey_name
  agent: string;                     // responder_agent_id, resolved by display name
  agent_id?: string;                 // stable agent UUID
  enabled?: boolean;                 // default true
  evaluator_instructions?: string;
  transcript?: HubAsCodeJourneyTurn[];
  /** Per-step overrides keyed by `step_id`. */
  step_config?: Record<string, HubAsCodeJourneyStepOverride>;
}

// =============================================
// Zod request schemas
// =============================================

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ciUuidSchema = z.string().regex(uuidPattern, 'must be a valid UUID');

/**
 * Walk `agents[].tools.custom[].config.parameters` and add draft-2020-12
 * violations as Zod issues (gh #1542). Placeholder-token cases are skipped
 * — see the matching logic in contracts/setup/tools.ts.
 */
function refineHubAsCodeCustomTools(
  config: unknown,
  ctx: z.RefinementCtx,
): void {
  const agents = (config as { agents?: unknown }).agents;
  if (!Array.isArray(agents)) return;

  for (let a = 0; a < agents.length; a++) {
    const agent = agents[a] as { tools?: { custom?: unknown } } | null | undefined;
    const custom = agent?.tools?.custom;
    if (!Array.isArray(custom)) continue;

    for (let c = 0; c < custom.length; c++) {
      const tool = custom[c] as { name?: string; config?: unknown } | null | undefined;
      const raw = tool?.config;
      if (raw == null) continue;

      const params = extractToolParameters(raw);
      if (params === undefined) {
        // Either `config` is not an object shape, or there's no `parameters`
        // field. Only the former is an error.
        const looksLikeObject = raw !== null
          && typeof raw === 'object'
          && !Array.isArray(raw)
          || (typeof raw === 'string' && (() => {
            try { const parsed = JSON.parse(raw); return parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed); }
            catch { return false; }
          })());
        if (!looksLikeObject) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['agents', a, 'tools', 'custom', c, 'config'],
            message: `custom tool "${tool?.name ?? '<unnamed>'}": config must be an object`,
          });
        }
        continue;
      }

      if (containsKanbanPlaceholders(params)) continue;

      const result = validateToolInputSchema(params);
      if (!result.ok) {
        for (const err of result.errors) {
          const tail = err.path.split('.').filter(Boolean);
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['agents', a, 'tools', 'custom', c, 'config', 'parameters', ...tail],
            message: `custom tool "${tool?.name ?? '<unnamed>'}": ${err.message}${err.suggestion ? ` — ${err.suggestion}` : ''}`,
          });
        }
      }
    }
  }
}

/**
 * Walk `hub.kanban_statuses` and re-path each `kanbanStatusesArraySchema`
 * issue under `['hub', 'kanban_statuses', ...]` so the CLI surfaces field-level
 * errors (e.g. "config.hub.kanban_statuses[2].triggersAgentResponse: …").
 *
 * The schema enforces cross-field invariants (mutually-exclusive flags, exactly
 * one initial status, unique names). See contracts/setup/kanban.ts.
 */
function refineHubAsCodeKanbanStatuses(
  config: unknown,
  ctx: z.RefinementCtx,
): void {
  const hub = (config as { hub?: { kanban_statuses?: unknown } }).hub;
  if (hub == null || !('kanban_statuses' in hub)) return;
  const statuses = hub.kanban_statuses;
  if (!Array.isArray(statuses)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['hub', 'kanban_statuses'],
      message:
        statuses === null
          ? 'kanban_statuses must be an array; use `[]` to clear or omit the key to leave it unchanged'
          : 'kanban_statuses must be an array',
    });
    return;
  }

  const result = kanbanStatusesArraySchema.safeParse(statuses);
  if (!result.success) {
    for (const issue of result.error.issues) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['hub', 'kanban_statuses', ...issue.path],
        message: issue.message,
      });
    }
  }
}

/**
 * Validate `hub.lanes` shape and the lane↔status cross-reference. Re-paths issues
 * under `['hub', 'lanes', ...]` (shape) or `['hub', 'kanban_statuses', ...]`
 * (cross-reference), mirroring `refineHubAsCodeKanbanStatuses`. Lanes are a hub
 * config field; on push the full hub config is present, so both arrays are in scope.
 */
function refineHubAsCodeLanes(config: unknown, ctx: z.RefinementCtx): void {
  const hub = (config as { hub?: { lanes?: unknown; kanban_statuses?: unknown } }).hub;
  if (hub == null) return;

  if ('lanes' in hub && hub.lanes != null) {
    if (!Array.isArray(hub.lanes)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['hub', 'lanes'], message: 'lanes must be an array' });
      return;
    }
    const result = lanesArraySchema.safeParse(hub.lanes);
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['hub', 'lanes', ...issue.path], message: issue.message });
      }
      return;
    }
  }

  // Cross-reference: lane_slug must point at a declared lane; initial status has none.
  const statuses = hub.kanban_statuses;
  if (Array.isArray(statuses)) {
    for (const issue of validateLaneReferences(hub.lanes as never, statuses as never)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['hub', 'kanban_statuses', ...issue.path], message: issue.message });
    }
  }
}

/**
 * Lightweight schema for HubAsCode config — validates structural requirements
 * (version, hub.name) + draft-2020-12 check on every custom tool's config
 * parameters (gh #1542) + kanban_statuses cross-field invariants.
 *
 * Both refines are combined in a single `.superRefine` because Zod's
 * `.superRefine` returns `ZodEffects`, which can't be re-chained without
 * `.pipe()`.
 */
const hubAsCodeConfigSchema = z.object({
  version: z.literal(1),
  hub: z.object({
    name: z.string().min(1, 'config.hub.name is required'),
  }).passthrough(),
}).passthrough().superRefine((config, ctx) => {
  refineHubAsCodeCustomTools(config, ctx);
  refineHubAsCodeKanbanStatuses(config, ctx);
  refineHubAsCodeLanes(config, ctx);
});

// --- Param schemas ---

export const ciPullHubIdParam = z.object({
  hub_id: ciUuidSchema,
});

export const ciBranchParam = z.object({
  branch: z.string().min(1),
});

// --- Query schemas ---

export const ciPullQuery = z.object({
  organization_id: ciUuidSchema.optional(),
});

export const ciHubsQuery = z.object({
  organization_id: ciUuidSchema,
});

export const ciLookupQuery = z.object({
  path: z.string().min(1, 'path query parameter is required'),
  organization_id: ciUuidSchema.optional(),
});

export const ciBranchesQuery = z.object({
  hub_id: ciUuidSchema,
});

// --- Body schemas ---

export const ciSyncBody = z.object({
  hub_id: ciUuidSchema,
  branch_name: z.string().min(1, 'branch_name is required'),
  config: hubAsCodeConfigSchema,
  commit_sha: z.string().optional(),
});

export const ciPushBody = z.object({
  hub_id: ciUuidSchema,
  config: hubAsCodeConfigSchema,
  organization_id: ciUuidSchema.optional(),
});

export const ciDiffBody = z.object({
  hub_id: ciUuidSchema,
  config: hubAsCodeConfigSchema,
  direction: z.enum(['push', 'pull']).default('push'),
  organization_id: ciUuidSchema.optional(),
});

export const ciPublishBody = z.object({
  hub_id: ciUuidSchema,
  branch_name: z.string().min(1, 'branch_name is required'),
});

export const ciPublishPreviewBody = z.object({
  hub_id: ciUuidSchema,
});

/**
 * POST /api/ci/sync-mcp — re-discover an MCP connection's tool/resource catalog.
 * `connection` is the display name (as written in `connections[].name`) or the
 * connection UUID; the route resolves either against the hub.
 */
export const ciSyncMcpBody = z.object({
  hub_id: ciUuidSchema,
  connection: z.string().min(1, 'connection is required'),
  organization_id: ciUuidSchema.optional(),
});

// --- Org-as-code (org-resources) schemas ---

export const ciOrgResourcesParam = z.object({
  org_id: ciUuidSchema,
});

/**
 * Lightweight passthrough schema for the org-resources payload — mirrors
 * `hubAsCodeConfigSchema`'s approach (validate structural requirements only;
 * the apply path does the authoritative shape work). Requires `version` and,
 * when present, `resources` to be an array.
 */
const orgResourcesConfigSchema = z.object({
  version: z.literal(1),
  resources: z.array(z.object({ name: z.string().min(1) }).passthrough()).optional(),
}).passthrough();

export const ciOrgResourcesPushBody = z.object({
  organization_id: ciUuidSchema,
  config: orgResourcesConfigSchema,
});

export const ciOrgResourcesDiffBody = z.object({
  organization_id: ciUuidSchema,
  config: orgResourcesConfigSchema,
  direction: z.enum(['push', 'pull']).default('push'),
});

// =============================================================================
// INFERRED TYPES
// =============================================================================

export type CiSyncInput = z.infer<typeof ciSyncBody>;
export type CiPushInput = z.infer<typeof ciPushBody>;
export type CiDiffInput = z.infer<typeof ciDiffBody>;
export type CiPublishInput = z.infer<typeof ciPublishBody>;
export type CiPublishPreviewInput = z.infer<typeof ciPublishPreviewBody>;
export type CiSyncMcpInput = z.infer<typeof ciSyncMcpBody>;
export type CiOrgResourcesPushInput = z.infer<typeof ciOrgResourcesPushBody>;
export type CiOrgResourcesDiffInput = z.infer<typeof ciOrgResourcesDiffBody>;

// =============================================
// Response types
// =============================================

export interface CiDiffResponse {
  has_changes: boolean;
  direction: 'push' | 'pull';
  summary: string;
  diff: unknown;
  /**
   * The hub's current server-owned `preview_label`. Surfaced so the CLI can warn
   * when a hand-edited `hub.yaml` label differs from the server — the diff engine
   * ignores `preview_label` (it's set via `wayai relabel`/replicate, not push), so
   * without this the silent no-op is invisible. Optional for back-compat with
   * older backends. See gh hub-branching.
   */
  preview_label?: string | null;
  /**
   * Non-fatal skip reasons surfaced by the diff engine (e.g. native/delegation
   * tools dropped because their connector has no connection on the hub).
   * Populated independently of `has_changes` so the CLI can display them even
   * when every change was silently dropped. Always present (possibly empty),
   * symmetric with `DiffResult.warnings`. See gh #1538.
   */
  warnings: string[];
}

export interface CiLookupResponse {
  hub_id: string;
  hub_name: string;
  hub_environment: string;
  preview_label?: string | null;
  branch_name?: string | null;
}

export interface CiOrganizationsResponse {
  organizations: Array<{ id: string; name: string }>;
}

export interface CiCreateOrganizationResponse {
  organization: {
    id: string;
    name: string;
    /** Resolved DO placement region (auto-detected from the request edge, or the
     *  caller's explicit `primary_region`). Surfaced so the CLI can show it. */
    region: string | null;
    /** True when the caller passed an explicit region; false when auto-detected
     *  (closest Cloudflare PoP). Lets the CLI phrase "closest to you" vs "as requested". */
    region_explicit: boolean;
  };
}

export interface CiHubListItem {
  hub_id: string;
  hub_name: string;
  hub_environment: string;
  preview_label: string | null;
  branch_name: string | null;
  production_hub_id: string | null;
}

export interface CiHubListResponse {
  hubs: CiHubListItem[];
}

export interface CiSyncResponse {
  preview_hub_id: string;
  created: boolean;
  changes: {
    hub_updated: boolean;
    connections_created: number;
    connections_updated: number;
    connections_deleted: number;
    agents_created: number;
    agents_updated: number;
    agents_deleted: number;
    tools_created: number;
    tools_updated: number;
    tools_deleted: number;
    states_created: number;
    states_updated: number;
    states_deleted: number;
    teams_created: number;
    teams_updated: number;
    teams_deleted: number;
    resources_created: number;
    resources_updated: number;
    resources_deleted: number;
    resource_files_uploaded: number;
    resource_files_deleted: number;
    resource_links_created: number;
    resource_links_updated: number;
    resource_links_deleted: number;
    evals_created: number;
    evals_updated: number;
    evals_deleted: number;
    journeys_created: number;
    journeys_updated: number;
    journeys_deleted: number;
    outbound_contacts_created: number;
    outbound_contacts_updated: number;
    outbound_contacts_deleted: number;
    outbound_lists_created: number;
    outbound_lists_updated: number;
    outbound_lists_deleted: number;
    outbound_schedules_created: number;
    outbound_schedules_updated: number;
    outbound_schedules_deleted: number;
  };
  errors?: string[];
  /** Non-blocking lint warnings (e.g. unreachable kanban statuses). Same
   *  `KanbanLintWarning` shape returned by REST `/hubs` endpoints so the CLI
   *  can render structured details (code, slug, message). */
  warnings?: Array<{ code: string; slug: string; name: string; message: string }>;
}

export interface CiPublishResponse {
  synced: boolean;
  production_hub_id: string;
}

export interface CiSyncMcpResponse {
  success: boolean;
  connection_id: string;
  connection_name: string;
  tools_count?: number;
  resources_count?: number;
  message?: string;
  warnings?: string[];
}

export interface CiPublishPreviewResponse {
  synced: boolean;
  production_hub_id?: string;
}

export interface CiBranchInfo {
  branch_name: string;
  preview_hub_id: string;
  last_ci_activity_at: string | null;
  ci_commit_sha: string | null;
}

export interface CiBranchListResponse {
  branches: CiBranchInfo[];
}

export interface CiBranchDeleteResponse {
  deleted: boolean;
  message?: string;
}

/** Pull returns the org-resources payload directly (CLI writes it to `org/`). */
export type CiOrgResourcesPullResponse = OrgResourcesPayload;

export interface CiOrgResourcesPushResponse {
  changes: {
    resources_created: number;
    resources_updated: number;
    resources_deleted: number;
    resource_files_uploaded: number;
    resource_files_deleted: number;
    resource_folders_created: number;
    resource_folders_deleted: number;
  };
  /** Aggregate awaited fan-out to linked hub shadows across all changed resources. */
  fanout: { hubs: number; failures: number };
  errors?: string[];
}
